import {
  Box,
  FormHelperText,
  FormHelperTextProps,
  InputLabel,
  InputLabelProps,
} from "@mui/material";
import {
  DatePicker,
  DateTimeValidationError,
  PickerValidDate,
} from "@mui/x-date-pickers";
import { FastField, Field, FieldProps } from "formik";
import { DateTime } from "luxon";
import { ReactElement } from "react";

import theme from "~frontend/theme";

export default function FormikDatePicker({
  name,
  label,
  timezone,
  minDate,
  minDateError,
  maxDate,
  maxDateError,
  fullWidth,
  helperText,
  format, // Format reference here: https://moment.github.io/luxon/#/formatting?id=table-of-tokens
  onChange,
  onError,
  disabled,
  shouldDisableDate,
  customError,
  fast,
  inputLabelProps,
  helperTextProps,
}: {
  name: string;
  label: string;
  timezone?: string;
  minDate?: DateTime;
  minDateError?: string;
  maxDate?: DateTime;
  maxDateError?: string;
  fullWidth?: boolean;
  helperText?: string;
  format?: string;
  onChange?: any;
  onError?: any;
  disabled?: boolean;
  customError?: { errorText: string };
  shouldDisableDate?: ((day: PickerValidDate) => boolean) | undefined;
  inputLabelProps?: InputLabelProps;
  helperTextProps?: FormHelperTextProps;
  fast?: boolean;
}): ReactElement {
  const maxMinErrorString = (error: DateTimeValidationError) => {
    switch (error) {
      case "minDate":
        return minDateError;
      case "maxDate":
        return maxDateError;
      default:
        return "";
    }
  };
  const Component = fast ? FastField : Field;

  return (
    <Component name={name}>
      {({ field, form, meta }: FieldProps<DateTime>) => {
        const error = customError ? customError.errorText : meta.error;
        return (
          <Box sx={{ width: fullWidth ? "100%" : undefined }}>
            {label ? (
              <InputLabel shrink {...inputLabelProps}>
                {label}
              </InputLabel>
            ) : null}
            <DatePicker
              value={field.value}
              timezone={timezone}
              onChange={
                onChange
                  ? onChange
                  : async newValue => {
                      await form.setFieldValue(field.name, newValue);
                    }
              }
              onError={
                onError
                  ? onError
                  : async newError => {
                      if (newError) {
                        form.setFieldError(
                          field.name,
                          maxMinErrorString(newError)
                        );
                        await form.setFieldTouched(field.name, true);
                      } else {
                        form.setFieldError(field.name, undefined);
                      }
                    }
              }
              minDate={minDate}
              maxDate={maxDate}
              sx={{
                paddingTop: label ? 0 : undefined,
                width: fullWidth ? "100%" : undefined,
                marginBottom: 0,
                "& .MuiInputAdornment-root": { width: "48px" },
              }}
              format={format}
              disabled={disabled}
              shouldDisableDate={
                shouldDisableDate
                  ? shouldDisableDate
                  : value => meta.touched && !!error && field.value === value
              }
            />
            {(meta.touched && error) || helperText ? (
              <FormHelperText
                sx={{
                  paddingLeft: theme.spacing(2),
                  paddingTop: 0,
                  marginTop: 0,
                  fontSize: "10px",
                }}
                error={meta.touched && !!error}
                {...helperTextProps}
              >
                {(meta.touched && error) || helperText}
              </FormHelperText>
            ) : null}
          </Box>
        );
      }}
    </Component>
  );
}
